import Storage from '@aws-amplify/storage';
import { Auth } from 'aws-amplify';
import {
  importReviewVisible,
  unmappedMarkerScanVisible,
} from '../../utils/importReviewVisibility';
import {
  ImportReview,
  ImportReviewPayload,
  ImportReviewResult,
} from './import-review';
import { faFolderOpen, faFolderUpload, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import './upload.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import React, { useRef, useState } from 'react';
import {
  Tooltip,
  OverlayTrigger,
  Button,
  Col,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';

Storage.configure({
  AWSS3: {
    bucket: 'labsmarts-next',
    region: 'us-east-1',
  },
});

/*
 * The vision read's authenticated endpoint, from lambdas/bloodworkVision.
 *
 * Unset means it is not deployed, which is why visionEnabled checks for it:
 * with no URL the picker stays single-file and a photograph is never accepted
 * at all, rather than accepted and then quietly dropped.
 */
const VISION_URL = process.env.REACT_APP_VISION_LAMBDA_URL || '';

/**
 * Why the read never came back.
 *
 * fetch() rejects a dropped connection as a bare "Failed to fetch", which reads
 * exactly like being offline and sends the practitioner to check their wifi for
 * a problem that was never theirs. The API in front of this read hangs up at
 * 30 seconds while the read itself runs for minutes on a long report, so on a
 * multi-page file that is the likely cause and it is worth naming rather than
 * blaming the network.
 */
function readLostMessage(err: any): string {
  const detail = (err && err.message) || 'connection lost';
  return (
    'The read did not come back. A report of more than a page or two currently '
    + 'takes longer than the connection allows, so the values could not be '
    + `collected (${detail}). The results already imported are unaffected.`
  );
}

/**
 * The practitioner's Cognito token, for the API in front of the read.
 *
 * The endpoint used to be a public function URL. That was fine when the thing
 * behind it was free; this one calls a model per page, so an open endpoint is
 * somebody else's bill. API Gateway rejects the call outright without this.
 */
async function authHeader(): Promise<{ [key: string]: string }> {
  const session: any = await Auth.currentSession();
  return { Authorization: `Bearer ${session.getIdToken().getJwtToken()}` };
}

/**
 * The practitioner's Cognito subject, which is the folder their uploads go in.
 *
 * The bucket's `public/` prefix is ONE namespace shared by every practitioner,
 * so an upload key carries no notion of who owns it. The vision Lambda has to
 * refuse a key belonging to somebody else — otherwise any signed-in
 * practitioner can have a stranger's lab report read aloud to them and get an
 * hour-long link to the original — and the only thing it can check the key
 * against is the subject the gateway verified on the request. So the subject
 * goes IN the key. See keyProblem() in lambdas/bloodworkVision/index.js.
 *
 * Read off the same ID token the Authorization header carries, so the two can
 * never disagree about who is calling.
 */
async function currentUserId(): Promise<string> {
  const session: any = await Auth.currentSession();
  const sub = session.getIdToken().payload?.sub;
  if (!sub) {
    throw new Error('No signed-in practitioner to file the upload under.');
  }
  return sub;
}

/**
 * Did the text parser actually get anything off this file?
 *
 * A scanned PDF — pictures in a PDF wrapper, which is what a phone's "Scan
 * Documents" produces — parses without error and yields nothing, so an empty
 * result is the signal to try reading it as a photograph rather than the signal
 * to give up.
 */
export function hasUsableResults(results: { [key: string]: any }): boolean {
  if (!results) return false;
  return Object.keys(results).some(key => {
    const value = results[key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
}

/**
 * Did the parser open the file and simply not know the lab?
 *
 * Exported so the rule can be tested on its own, because getting it wrong is
 * invisible: a false negative just restores the old behavior of calling an
 * unsupported lab an unreadable file, which is the bug this distinguishes.
 *
 * Deliberately narrow. Only the parser's own UNRECOGNIZED_LAB says the document
 * was read and matched nothing; a 500, a dropped connection or an S3 failure
 * are all "we broke", and telling a practitioner their file is unsupported when
 * the bucket was down would send them to re-export a file that was always fine.
 */
export function isUnrecognizedLab(ok: boolean, payload: any): boolean {
  return !ok && payload?.code === 'UNRECOGNIZED_LAB';
}

/**
 * Which reader each picked file belongs to.
 *
 * Exported so the rule can be tested on its own: it decides whether a Labcorp
 * PDF reaches the parser that can read it, and getting it wrong fails quietly —
 * the wrong reader returns nothing rather than an error.
 */
export function routeUploads<T extends { type?: string; name?: string }>(
  files: T[],
): { documents: T[]; images: T[] } {
  const isImage = (f: T) =>
    /^image\//.test(f.type || '') ||
    // A file dragged from some scanners arrives with an empty MIME type.
    (!f.type && /\.(jpe?g|png|heic|heif|webp|tiff?)$/i.test(f.name || ''));
  return {
    documents: files.filter(f => !isImage(f)),
    images: files.filter(isImage),
  };
}

export const Upload = ({
  setState,
  clearForm,
  showModal,
  isUploadAvailable,
  /*
   * Present only while importReviewVisible() is on. A photographed report is
   * read by a vision model rather than parsed from a text layer, so its values
   * go through a confirmation screen before they reach the form — see
   * import-review.tsx. A Labcorp PDF still takes the setState path untouched.
   */
  onReviewImport = undefined,
}) => {
  const inputFile = useRef(null);

  const [lab, setLab] = useState('Labcorp');
  const [file, setFile] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState<ImportReviewPayload>(undefined);
  /*
   * What the free text parser just imported, kept so the practitioner can
   * choose to spend a vision read on the same file afterwards. Holding the S3
   * keys means that costs a read rather than a second upload.
   */
  const [deepScan, setDeepScan] = useState<{
    /*
     * The FILES, not the keys the free parse used. Those keys are bare
     * filenames in a namespace every practitioner shares, and the Lambda
     * refuses to read anything outside the caller's own folder — so the scan
     * re-files them under the uploader like any other vision read. One extra
     * PUT, and the ownership check keeps working.
     */
    files: File[];
    fileName: string;
    knownMarkers: string[];
    /**
     * Why the offer is being made, so it can explain itself.
     *
     * Absent means the parse succeeded and this is the ordinary "pick up what
     * the parser cannot see" offer. 'empty' is a file with no text layer, which
     * is nearly always a scan. 'unrecognized' is a document that read fine and
     * matched no lab — an unsupported lab, or not a lab report at all. Those
     * two used to be one message and they call for different things.
     */
    reason?: 'empty' | 'unrecognized';
  }>(undefined);
  const [error, setError] = useState<string>(undefined);
  /*
   * Guards against a stale read landing on the form. Uploading ten photographs
   * takes long enough that a practitioner can give up and pick a different set,
   * and the legacy path's filename check cannot tell two batches apart.
   */
  const requestToken = useRef(0);

  /*
   * Two gates over one reader, because they are two decisions.
   *
   * photoImportEnabled is the whole photographed-report import: the multi-file
   * picker, and routing images to the vision read. On hold.
   *
   * scanEnabled is only the button offered after a PDF has been parsed for
   * free — the one path by which a marker LabSmarts has no field for can reach
   * a test at all. Live where the gate allows it.
   */
  const photoImportEnabled =
    importReviewVisible() && !!onReviewImport && !!VISION_URL;
  const scanEnabled =
    unmappedMarkerScanVisible() && !!onReviewImport && !!VISION_URL;

  const openUploadPDFInfoWindow = () => {
    window.open('https://labsmarts.com/upload_test_results');
  };

  //openUploadPDFInfoWindow = () => {
  //  window.open('https://labsmarts.com/upload_test_results');
  //}

  /*
   * A PDF is parsed for free; a photograph has to be read by the vision model.
   *
   * Running every PDF through the vision reader was tried and reverted. It is
   * genuinely better at the job — markers LabSmarts has no field for,
   * qualitative results, the confirmation screen — but it costs real money per
   * report where parsing text costs nothing, and most of what it returns for a
   * Labcorp PDF is markers the parser already got right.
   *
   * So the free parser runs first and the vision read becomes something the
   * practitioner ASKS for, on the file already uploaded, when they want the
   * markers LabSmarts has no field for. See the "Scan for more markers" button.
   */
  const handleUpload = async e => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length === 0) return;
    setDeepScan(undefined);

    // Without the vision reader the picker is single-file, so this is the
    // one-PDF path running exactly as it always has.
    if (!photoImportEnabled) return handleLegacyUpload(files[0]);

    const { documents, images } = routeUploads(files);
    for (let i = 0; i < documents.length; i++) {
      await handleLegacyUpload(documents[i], { batched: files.length > 1 });
    }
    // A photograph has no free path — the parser has never been able to read
    // one — so it goes straight to the vision reader.
    if (images.length > 0) await handleVisionUpload(images);
  };

  const handleLegacyUpload = async (
    newFile,
    options: { batched?: boolean; noFallback?: boolean } = {},
  ) => {
    if (!newFile) return;

    const name = newFile.name?.replaceAll(' ', '_');
    const token = ++requestToken.current;

    setLoading(true);
    // Cleared as the new upload STARTS, not when it finishes. Without this a
    // failed parse left its red message sitting over the next file's results,
    // which reads as the new import having failed too.
    setError(undefined);
    setFile(newFile?.name);

    try {
      console.log('Uploading file: ', await Storage.put(name, newFile));

      const res = await fetch(
        process.env.REACT_APP_ENV === 'prod'
          ? 'https://fr3g7xel6oznoyhlvtu2ohqr6u0cbwao.lambda-url.us-east-1.on.aws/'
          : 'https://b6oxxdcpa6zsxddhvnqa7f55zy0kushi.lambda-url.us-east-1.on.aws/',
        { method: 'POST', body: JSON.stringify({ lab, name }) },
      );
      const payload = await res.json();

      /*
       * A document the parser opened and did not recognize.
       *
       * Handled before anything reads `fields`, because an error body has none
       * and destructuring it threw — which is exactly how a lab we simply have
       * no parser for came to report itself as an unreadable file. Matched on
       * the request token rather than the filename, since the token is set on
       * every call and a failure may carry no filename at all.
       */
      if (!res.ok) {
        if (token !== requestToken.current) return;
        setLoading(false);
        const unrecognized = isUnrecognizedLab(res.ok, payload);
        if (unrecognized && scanEnabled && !options.noFallback) {
          setDeepScan({
            files: [newFile],
            fileName: newFile.name,
            knownMarkers: [],
            reason: 'unrecognized',
          });
          return;
        }
        setError(
          unrecognized
            ? 'That does not look like a lab report LabSmarts can read yet.'
            : 'That file could not be read.',
        );
        return;
      }

      const { fileName, results, fields } = payload;
      const collectionDate = fields?.collectionDate;

      /*
       * Is this response still about what the practitioner is waiting for?
       *
       * On its own, a single upload can be checked against the picker's value.
       * In a batch that check is useless — the input reports only the FIRST
       * file picked, so every later PDF would look like a stale response and be
       * dropped — so a batch is matched on the request token instead.
       */
      const stillWanted = options.batched
        ? token === requestToken.current
        : inputFile.current.value?.replaceAll(' ', '_')?.endsWith(fileName);

      if (!stillWanted) return;

      /*
       * A PDF the text parser got nothing out of is very often a SCAN: a phone
       * photographed the pages and wrapped them in a PDF, so there is no text
       * layer to parse. Rather than report a failure the practitioner cannot
       * act on, read it as photographs — the file is already in the bucket, so
       * the retry costs a read, not another upload.
       */
      if (!hasUsableResults(results)) {
        /*
         * Hand it to the vision reader as page images. noFallback is what stops
         * the two readers passing the same file back and forth: the vision path
         * sets it when IT falls back to this parser.
         *
         * The file is re-uploaded rather than read from the key it already has.
         * That key is a bare filename in a namespace every practitioner shares,
         * and the vision Lambda (rightly) refuses to read anything outside the
         * caller's own folder — so the saving of one upload would cost the
         * ownership check that makes the read safe. The bytes are still in
         * memory here, so the retry is a second PUT, not a second file picker.
         */
        if (photoImportEnabled && !options.noFallback) {
          return handleVisionUpload([newFile], { fromDocument: true });
        }
        setLoading(false);
        if (scanEnabled && !options.noFallback) {
          /*
           * Offer the read rather than running it. It costs a model call per
           * page, and spending that on a practitioner's behalf because a parse
           * came back empty is not a decision to make for them.
           */
          setDeepScan({
            files: [newFile],
            fileName: newFile.name,
            knownMarkers: [],
            reason: 'empty',
          });
          return;
        }
        setError(
          'Nothing could be read from that file. If it is a scan or a photo of a printout, upload the page images instead.',
        );
        return;
      }

      setLoading(false);
      setState({
        ...results,
        collectionDate: moment(collectionDate).startOf('day').toISOString(),
        collectionDateForUI: moment(collectionDate).toDate(),
      });
      // Offer the paid read on what the free one just handled.
      setDeepScan(prev => ({
        files: [...((prev && prev.files) || []), newFile],
        fileName: newFile.name,
        knownMarkers: [
          ...((prev && prev.knownMarkers) || []),
          ...Object.keys(results || {}),
        ],
      }));
    } catch (error) {
      console.error(error);
      if (token !== requestToken.current) return;
      setLoading(false);
      setError('That file could not be read.');
    }
  };

  /*
   * Every page is uploaded under one folder for this batch, so two
   * practitioners photographing their own "IMG_0042.jpg" cannot overwrite each
   * other. The batch folder sits inside a folder named for the practitioner,
   * which is what lets the Lambda tell whose upload a key is before it reads it
   * or signs a URL for it — see currentUserId above.
   */
  const handleVisionUpload = async (
    files: File[],
    /**
     * This batch is a document the text parser already failed on, so a failed
     * vision read must NOT hand it back — see the fallback in
     * handleLegacyUpload. Without it the two readers take turns on one file.
     */
    options: { fromDocument?: boolean; knownMarkers?: string[] } = {},
  ) => {
    const token = ++requestToken.current;
    setLoading(true);
    setError(undefined);
    setDeepScan(undefined);
    setFile(files.length === 1 ? files[0].name : `${files.length} pages`);

    const batch = uuidv4();
    const names: string[] = [];
    try {
      const owner = await currentUserId();
      for (let i = 0; i < files.length; i++) {
        const safe = files[i].name.replace(/ /g, '_');
        const key = `bloodwork-uploads/${owner}/${batch}/${String(i).padStart(2, '0')}-${safe}`;
        await Storage.put(key, files[i], { contentType: files[i].type });
        names.push(key);
      }
    } catch (err) {
      console.error(err);
      if (token !== requestToken.current) return;
      setLoading(false);
      setError('The upload did not finish. Check your connection and try again.');
      return;
    }
    return readAsPhotographs(
      names,
      files[0].name,
      token,
      files,
      options.knownMarkers || [],
      options,
    );
  };

  /**
   * Reads already-uploaded S3 keys as photographed pages.
   *
   * Separate from the upload so the scanned-PDF fallback can reuse it: that
   * file is already in the bucket, so the retry costs a read rather than a
   * second upload.
   */
  const readAsPhotographs = async (
    names: string[],
    fileName: string,
    token: number,
    /** The files this batch came from, so PDFs can fall back to the parser. */
    sourceFiles: File[] = [],
    /** Markers the free parser already imported, so they are not offered twice. */
    knownMarkers: string[] = [],
    /** See handleVisionUpload — stops the two readers ping-ponging one file. */
    options: { fromDocument?: boolean } = {},
  ) => {
    try {
      const res = await fetch(VISION_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ lab, names, fileName, knownMarkers }),
      });
      const payload = await res.json();

      // The practitioner moved on; whatever came back is about a batch they
      // are no longer looking at.
      if (token !== requestToken.current) return;
      setLoading(false);

      if (!res.ok || !payload?.review?.rows?.length) {
        /*
         * Nothing came back. If every file was a document, the text parser is
         * still there and may well read it — that is the whole point of leaving
         * it in place. noFallback stops the two readers handing the file back
         * and forth.
         */
        const { documents, images } = routeUploads(sourceFiles);
        // Already been through the parser once — that is why it is here — so
        // sending it back would parse the same empty file a second time and
        // land on the same message a step later.
        if (!options.fromDocument && documents.length > 0 && images.length === 0) {
          for (let i = 0; i < documents.length; i++) {
            await handleLegacyUpload(documents[i], {
              batched: documents.length > 1,
              noFallback: true,
            });
          }
          return;
        }
        setError(
          payload?.message ||
            'That report could not be read. Try clearer photographs of each page.',
        );
        return;
      }
      setReview({ ...payload.review, fileName });
    } catch (err) {
      console.error(err);
      if (token !== requestToken.current) return;
      setLoading(false);
      setError(readLostMessage(err));
    }
  };

  /** The paid read, on a file the free parser has already been through. */
  const runDeepScan = async () => {
    if (!deepScan) return;
    // Captured first: handleVisionUpload clears the offer as it starts.
    const { files, knownMarkers } = deepScan;
    await handleVisionUpload(files, { fromDocument: true, knownMarkers });
  };

  const handleCancel = () => {
    clearForm();
    setFile(null);
    setLoading(false);
    setError(undefined);
    setDeepScan(undefined);
    requestToken.current += 1;
    inputFile.current.value = null;
  };

  const panelTooltip = (
    <Tooltip id="modal-tooltip">
      Click this icon to learn more about the labs we support.
    </Tooltip>
  );

  const applyReview = (result: ImportReviewResult) => {
    setReview(undefined);
    onReviewImport(result);
  };

  return (
    <>
      {review && (
        <ImportReview
          payload={review}
          show={true}
          onCancel={() => {
            setReview(undefined);
            handleCancel();
          }}
          onImport={applyReview}
        />
      )}
      {/* <Form.Group as={Row} key="lab-group" controlId="lab-group">
        <Form.Label column sm>
          Lab
        </Form.Label>
        <Col sm>
          <DropdownButton id="dropdown-basic-button" title={lab}>
            <Dropdown.Item onClick={() => setLab('Labcorp')}>
              Labcorp
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setLab('Labcorp Old')}>
              Labcorp Old
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setLab('Quest')}>Quest</Dropdown.Item>
          </DropdownButton>
        </Col>
        <Col sm></Col>
      </Form.Group> */}
      <Form.Group
        as={Row}
        key="upload-group"
        controlId="upload-group"
        className="d-flex align-items-center"
      >
        <Form.Label column sm={4}>
          Upload Test Results&nbsp;
          <OverlayTrigger overlay={panelTooltip}>
            <FontAwesomeIcon
              className="external-link"
              icon="external-link-alt"
              onClick={openUploadPDFInfoWindow}
            />
          </OverlayTrigger>
        </Form.Label>
        <Col className="d-flex flex-wrap align-items-center">
          <Button
            variant="primary"
            disabled={loading}
            className="d-flex align-items-center"
            onClick={() =>
              isUploadAvailable ? inputFile.current.click() : showModal()
            }
          >
            <FontAwesomeIcon
              icon={file ? faFolderUpload : faFolderOpen}
              className="mr-3"
            />
            Browse
            <Spinner
              className="ml-3"
              animation="border"
              size="sm"
              hidden={!loading}
            />
          </Button>
          <input
            hidden
            type="file"
            ref={inputFile}
            onChange={handleUpload}
            className="form-control-file"
            /*
             * Only while the vision read is switched on. Left off, the picker
             * behaves exactly as it does in prod today: one file, no filter.
             */
            multiple={photoImportEnabled}
            accept={
              photoImportEnabled ? 'image/*,application/pdf' : undefined
            }
          />
          {error && (
            <b className="ml-4 text-danger">
              {error}{' '}
              <Button variant="link" onClick={handleCancel}>
                Clear
              </Button>
            </b>
          )}
          {!error && file && (
            <b className="ml-4">
              {file}{' '}
              <Button variant="link" onClick={handleCancel}>
                {loading ? 'Cancel' : 'Clear'}
              </Button>
            </b>
          )}
          {!error && !loading && scanEnabled && deepScan && (
            <div className="upload-deep-scan">
              <Button
                variant="outline-primary"
                size="sm"
                className="upload-deep-scan-btn"
                onClick={runDeepScan}
              >
                <FontAwesomeIcon icon={faSparkles} className="mr-2" />
                Scan for markers LabSmarts doesn&rsquo;t have
              </Button>
              <span className="upload-deep-scan-note">
                {deepScan.reason === 'empty' &&
                  'Nothing could be read from that file. If it is a scan or a photo of a printout, reading the pages with AI usually works.'}
                {deepScan.reason === 'unrecognized' &&
                  'That does not look like a lab report LabSmarts can read yet. If it is one, reading the pages with AI usually works.'}
                {!deepScan.reason &&
                  'Reads the pages with AI to pick up markers and word results the parser cannot see. Takes a moment, and costs a little.'}
              </span>
            </div>
          )}
        </Col>
      </Form.Group>
    </>
  );
};
